import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import announcementService from "../../../services/announcementService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const AnnouncementList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} announcements`,
  });

  const [filters, setFilters] = useState({ searchText: "" });

  // Searched once the typing settles, on the same 300ms the tags and users
  // lists use, rather than one request per keystroke.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search cannot overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getAnnouncements = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        const response = await announcementService.getAnnouncements(
          requestParams
        );
        if (requestId !== requestSeqRef.current) return;

        if (response.data && response.data.status) {
          const rows = response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          setAnnouncements(Array.isArray(rows) ? rows : []);
          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch announcements" });
          setAnnouncements([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching announcements:", error);
        notify.error({ message: "Failed to fetch announcements" });
        setAnnouncements([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one
        // finishing early would show the list as loaded while it still loads.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.searchText]
  );

  // Mount only: every later fetch is an explicit call from a search or
  // pagination handler, so depending on getAnnouncements here would fire a
  // second redundant request alongside each of them.
  useEffect(() => {
    setPageTitle("Announcements");
    getAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getAnnouncements({ searchText: value.trim(), currentPage: 1 });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter / the search icon applies immediately, cancelling any pending
  // debounce so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getAnnouncements({
      searchText: (filters.searchText || "").trim(),
      currentPage: 1,
    });
  };

  const handleTableChange = (paginationInfo) => {
    const { current, pageSize } = paginationInfo;
    setPagination((prev) => ({ ...prev, current, pageSize }));
    getAnnouncements({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "announcement_title",
      key: "announcement_title",
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text || "-"}
        </Text>
      ),
    },
    {
      // The card's whole second line, separator included — see m_announcement.
      title: "Subtitle",
      dataIndex: "announcement_subtitle",
      key: "announcement_subtitle",
      render: (text) => <Text type="secondary">{text || "-"}</Text>,
    },
    {
      // The CMS's own label for the row, not shown to viewers.
      title: "Internal Name",
      dataIndex: "announcement_name",
      key: "announcement_name",
      responsive: ["lg"],
      render: (text) => <Text type="secondary">{text || "-"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "announcement_is_active",
      key: "announcement_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() =>
            navigate(`/announcements/edit/${record.m_announcement_id}`)
          }
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Announcements ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Search
            placeholder="Search announcements..."
            allowClear
            value={filters.searchText}
            onChange={handleSearchChange}
            onSearch={handleSearchNow}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/announcements/add/new")}
            size="middle"
          >
            Add Announcement
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={announcements}
        rowKey="m_announcement_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
        locale={{
          emptyText: "No announcements found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default AnnouncementList;
